import { DEFAULT_TEMPLATE_FOR_PRODUCT } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Image,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useCallback, useRef, useState } from "react"
import { CampaignChronicleView } from "../../components/CampaignChronicleView"
import { CharacterJournalView } from "../../components/CharacterJournalView"
import { FactionsView } from "../../components/FactionsView"
import { LocationsView } from "../../components/LocationsView"
import { NewMeetingDialog } from "../../components/NewMeetingDialog"
import { NPCsView } from "../../components/NPCsView"
import { VoxCampaignHero } from "../../components/VoxCampaignHero"
import { VoxPillButton } from "../../components/VoxPillButton"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import type { ICharacter } from "../../lib/api"
import {
  createCharacter,
  deleteCharacter,
  fetchCharacters,
  fetchFactions,
  fetchLocations,
  fetchNpcs,
  fetchSeriesById,
  fetchSeriesMembers,
  updateCharacter,
  updateSeries,
  uploadCharacterAvatar,
  uploadCharacterFullBody,
} from "../../lib/api"
import { queryKeys } from "../../lib/query-keys"
import { speakerInitials } from "../../lib/speaker-format"
import { detectProduct, useProduct } from "../../lib/tenant"
import type { ISeries } from "../../lib/types/template"
import styles from "./campaigns_.$id.module.css"
import { voxcanonOnlyRedirect } from "./voxcanon-only"

interface IMember {
  id: string
  name: string
  email: string
}

interface ICharacterFormValues {
  name: string
  role: "player" | "dm"
  userId: string
  playerName: string
  characterClass: string
  playStyle: string
  backstory: string
  imagePrompt: string
}

interface ISettingsFormValues {
  imageStyle: string
  systemPrompt: string
  setting: string
  tone: string
  dmUserId: string | null
}

type TPlayStyle = "combat" | "roleplay" | "puzzle" | "explorer"

const PLAY_STYLE_OPTIONS = [
  { value: "combat", label: "Combat" },
  { value: "roleplay", label: "Roleplay" },
  { value: "puzzle", label: "Puzzle" },
  { value: "explorer", label: "Explorer" },
]

const PLAY_STYLE_COLORS: Record<TPlayStyle, string> = {
  combat: "red",
  roleplay: "teal",
  puzzle: "yellow",
  explorer: "green",
}

function getPlayStyleColor(style: string | null): string {
  if (!style) return "gray"
  return PLAY_STYLE_COLORS[style as TPlayStyle] ?? "gray"
}

const EMPTY_CHARACTER_FORM: ICharacterFormValues = {
  name: "",
  role: "player",
  userId: "",
  playerName: "",
  characterClass: "",
  playStyle: "",
  backstory: "",
  imagePrompt: "",
}

function CampaignDetailPage(): React.JSX.Element {
  const product = useProduct()
  const { id } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const { i18n } = useLingui()
  const activeTab = search.tab ?? "overview"
  const handleTabChange = useCallback(
    (value: string | null): void => {
      const next = (value ?? "overview") as NonNullable<ICampaignSearch["tab"]>
      navigate({ search: () => ({ tab: next }), replace: true })
    },
    [navigate],
  )

  const [characterModalOpen, setCharacterModalOpen] = useState(false)
  // The campaign-hero "Begin session" CTA opens the new-session dialog (the same
  // one the dashboard uses); the session's series is chosen inside the dialog.
  const [showNewSession, setShowNewSession] = useState(false)
  const [editingCharacter, setEditingCharacter] = useState<ICharacter | null>(null)

  // Pending image files (client-side, uploaded on submit)
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null)
  const [pendingAvatarPreview, setPendingAvatarPreview] = useState<string | null>(null)
  const [pendingFullBodyFile, setPendingFullBodyFile] = useState<File | null>(null)
  const [pendingFullBodyPreview, setPendingFullBodyPreview] = useState<string | null>(null)

  const avatarInputRef = useRef<HTMLInputElement>(null)
  const fullBodyInputRef = useRef<HTMLInputElement>(null)

  const characterForm = useForm<ICharacterFormValues>({
    mode: "uncontrolled",
    initialValues: EMPTY_CHARACTER_FORM,
    validate: {
      name: (value) => (value.trim().length === 0 ? i18n._(msg`Character name is required`) : null),
    },
  })

  const settingsForm = useForm<ISettingsFormValues>({
    mode: "uncontrolled",
    initialValues: { imageStyle: "", systemPrompt: "", setting: "", tone: "", dmUserId: null },
  })

  // Org-id-first factory keys (queryKeys.seriesDetail / seriesCharacters / …).
  const { data: series, isLoading: seriesLoading } = useCompanyQuery<ISeries>({
    key: (org) => queryKeys.seriesDetail(org, id),
    queryFn: (company) => fetchSeriesById(company, id),
  })

  const { data: characters, isLoading: charactersLoading } = useCompanyQuery<ICharacter[]>({
    key: (org) => queryKeys.seriesCharacters(org, id),
    queryFn: (company) => fetchCharacters(company, id),
  })

  const { data: members } = useCompanyQuery<IMember[]>({
    key: (org) => queryKeys.seriesMembers(org, id),
    queryFn: (company) => fetchSeriesMembers(company, id),
  })

  const memberOptions = (members ?? []).map((m) => ({
    value: m.id,
    label: m.name || m.email,
  }))

  // World queries — drive pending-count badges on each tab. They share the
  // org-id-first factory keys with the child world Views (NPCsView / … ), so
  // React Query dedupes to a single fetch once a tab has loaded, and a world
  // entity mutation (which invalidates the same factory key) refreshes the
  // badge count here too.
  const { data: npcs } = useCompanyQuery({
    key: (org) => queryKeys.worldNpcs(org, id),
    queryFn: (company) => fetchNpcs(company, id),
  })
  const { data: locations } = useCompanyQuery({
    key: (org) => queryKeys.worldLocations(org, id),
    queryFn: (company) => fetchLocations(company, id),
  })
  const { data: factions } = useCompanyQuery({
    key: (org) => queryKeys.worldFactions(org, id),
    queryFn: (company) => fetchFactions(company, id),
  })
  const pendingNpcsCount = (npcs ?? []).filter((n) => n.pending).length
  const pendingLocationsCount = (locations ?? []).filter((l) => l.pending).length
  const pendingFactionsCount = (factions ?? []).filter((f) => f.pending).length

  const dmUserId = (series?.metadata?.dm_user_id as string | undefined) ?? undefined
  const dmName = dmUserId ? (members?.find((m) => m.id === dmUserId)?.name ?? null) : null

  const updateSeriesMutation = useCompanyMutation({
    mutationFn: (company, values: ISettingsFormValues) => {
      const existingMeta = series?.metadata ?? {}
      return updateSeries(company, id, {
        metadata: {
          ...existingMeta,
          imageStyle: values.imageStyle || undefined,
          systemPrompt: values.systemPrompt || undefined,
          setting: values.setting || undefined,
          tone: values.tone || undefined,
          dm_user_id: values.dmUserId || undefined,
        },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.seriesDetail(orgId, id) })
      notifications.show({
        title: i18n._(msg`Settings saved`),
        message: i18n._(msg`Campaign settings have been updated.`),
        color: "success",
      })
    },
    onError: (error: Error) => {
      notifications.show({
        title: i18n._(msg`Failed to save settings`),
        message: error.message,
        color: "danger",
      })
    },
  })

  const createCharacterMutation = useCompanyMutation({
    mutationFn: async (company, values: ICharacterFormValues) => {
      const isDm = values.role === "dm"
      const result = (await createCharacter(company, id, {
        name: values.name,
        userId: isDm ? null : values.userId || null,
        playerName: values.playerName || undefined,
        characterClass: values.characterClass || undefined,
        playStyle: values.playStyle || undefined,
        backstory: values.backstory || undefined,
        imagePrompt: values.imagePrompt || undefined,
        metadata: { isDm },
      })) as { id: string }

      // Upload images after character is created
      if (pendingAvatarFile) {
        await uploadCharacterAvatar(company, id, result.id, pendingAvatarFile)
      }
      if (pendingFullBodyFile) {
        await uploadCharacterFullBody(company, id, result.id, pendingFullBodyFile)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.seriesCharacters(orgId, id) })
      notifications.show({
        title: i18n._(msg`Character added`),
        message: i18n._(msg`Character has been added to the campaign.`),
        color: "success",
      })
      handleCharacterModalClose()
    },
    onError: (error: Error) => {
      notifications.show({ title: i18n._(msg`Failed to add character`), message: error.message, color: "danger" })
    },
  })

  const updateCharacterMutation = useCompanyMutation({
    mutationFn: async (company, values: ICharacterFormValues) => {
      if (!editingCharacter) throw new Error("no character selected")

      const isDm = values.role === "dm"
      await updateCharacter(company, id, editingCharacter.id, {
        name: values.name,
        userId: isDm ? null : values.userId || null,
        playerName: values.playerName || undefined,
        characterClass: values.characterClass || undefined,
        playStyle: values.playStyle || undefined,
        backstory: values.backstory || undefined,
        imagePrompt: values.imagePrompt || undefined,
        metadata: { isDm },
      })

      if (pendingAvatarFile) {
        await uploadCharacterAvatar(company, id, editingCharacter.id, pendingAvatarFile)
      }
      if (pendingFullBodyFile) {
        await uploadCharacterFullBody(company, id, editingCharacter.id, pendingFullBodyFile)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.seriesCharacters(orgId, id) })
      notifications.show({
        title: i18n._(msg`Character updated`),
        message: i18n._(msg`Character details have been saved.`),
        color: "success",
      })
      handleCharacterModalClose()
    },
    onError: (error: Error) => {
      notifications.show({
        title: i18n._(msg`Failed to update character`),
        message: error.message,
        color: "danger",
      })
    },
  })

  const deleteCharacterMutation = useCompanyMutation({
    mutationFn: (company, charId: string) => deleteCharacter(company, id, charId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.seriesCharacters(orgId, id) })
      notifications.show({
        title: i18n._(msg`Character removed`),
        message: i18n._(msg`The character has been removed.`),
        color: "warning",
      })
    },
    onError: (error: Error) => {
      notifications.show({
        title: i18n._(msg`Failed to remove character`),
        message: error.message,
        color: "danger",
      })
    },
  })

  const handleOpenCreateCharacter = useCallback((): void => {
    setEditingCharacter(null)
    characterForm.setValues(EMPTY_CHARACTER_FORM)
    setPendingAvatarFile(null)
    setPendingAvatarPreview(null)
    setPendingFullBodyFile(null)
    setPendingFullBodyPreview(null)
    setCharacterModalOpen(true)
  }, [characterForm])

  const handleOpenEditCharacter = useCallback(
    (character: ICharacter): void => {
      setEditingCharacter(character)
      const isDm = character.metadata?.isDm === true
      characterForm.setValues({
        name: character.name,
        role: isDm ? "dm" : "player",
        userId: character.userId ?? "",
        playerName: character.playerName ?? "",
        characterClass: character.characterClass ?? "",
        playStyle: character.playStyle ?? "",
        backstory: character.backstory ?? "",
        imagePrompt: character.imagePrompt ?? "",
      })
      setPendingAvatarFile(null)
      setPendingAvatarPreview(null)
      setPendingFullBodyFile(null)
      setPendingFullBodyPreview(null)
      setCharacterModalOpen(true)
    },
    [characterForm],
  )

  const handleCharacterModalClose = useCallback((): void => {
    setCharacterModalOpen(false)
    setEditingCharacter(null)
    characterForm.reset()
    setPendingAvatarFile(null)
    setPendingAvatarPreview(null)
    setPendingFullBodyFile(null)
    setPendingFullBodyPreview(null)
  }, [characterForm])

  const handleCharacterSubmit = useCallback(
    (values: ICharacterFormValues): void => {
      if (editingCharacter) {
        updateCharacterMutation.mutate(values)
      } else {
        createCharacterMutation.mutate(values)
      }
    },
    [editingCharacter, createCharacterMutation, updateCharacterMutation],
  )

  const handleDeleteCharacter = useCallback(
    (character: ICharacter): void => {
      modals.openConfirmModal({
        title: i18n._(msg`Remove character`),
        children: (
          <Text size="sm">
            <Trans>
              Are you sure you want to remove <strong>{character.name}</strong> from this campaign?
            </Trans>
          </Text>
        ),
        labels: { confirm: i18n._(msg`Remove`), cancel: i18n._(msg`Cancel`) },
        confirmProps: { color: "danger" },
        onConfirm: () => deleteCharacterMutation.mutate(character.id),
      })
    },
    [deleteCharacterMutation, i18n],
  )

  const handleAvatarFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0]
    if (!file) return
    setPendingAvatarFile(file)
    const preview = URL.createObjectURL(file)
    setPendingAvatarPreview(preview)
  }, [])

  const handleFullBodyFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0]
    if (!file) return
    setPendingFullBodyFile(file)
    const preview = URL.createObjectURL(file)
    setPendingFullBodyPreview(preview)
  }, [])

  const handleSettingsTabOpen = useCallback((): void => {
    if (!series) return
    const meta = series.metadata ?? {}
    settingsForm.setValues({
      imageStyle: (meta.imageStyle as string) ?? "",
      dmUserId: (meta.dm_user_id as string | null | undefined) ?? null,
      systemPrompt: (meta.systemPrompt as string) ?? "",
      setting: (meta.setting as string) ?? "",
      tone: (meta.tone as string) ?? "",
    })
  }, [series, settingsForm])

  if (seriesLoading) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    )
  }

  if (!series) {
    return (
      <Container size="lg" className={styles.page}>
        <Alert color="danger" variant="light">
          <Trans>Campaign not found.</Trans>
        </Alert>
      </Container>
    )
  }

  const isCharacterMutating = createCharacterMutation.isPending || updateCharacterMutation.isPending

  // Current avatar URL to show in the modal (pending preview takes priority)
  const currentAvatarUrl = pendingAvatarPreview ?? editingCharacter?.avatarUrl ?? null
  const currentFullBodyUrl = pendingFullBodyPreview ?? editingCharacter?.fullBodyUrl ?? null

  return (
    <Container size="lg" className={styles.page}>
      <Group mb="md">
        <Button component={Link} to="/campaigns" variant="subtle" size="sm">
          ← <Trans>Back to campaigns</Trans>
        </Button>
      </Group>

      {product === "voxcanon" ? (
        (() => {
          const meta = series.metadata ?? {}
          const heroImage =
            (typeof meta.imageUrl === "string" && meta.imageUrl) ||
            (typeof meta.image_url === "string" && meta.image_url) ||
            null
          const explicitSystem = typeof meta.system === "string" ? meta.system.trim() : ""
          const systemLabel = explicitSystem
            ? explicitSystem.toUpperCase()
            : series.templateId === DEFAULT_TEMPLATE_FOR_PRODUCT.voxcanon
              ? "D&D 5E"
              : series.templateId === DEFAULT_TEMPLATE_FOR_PRODUCT.lexicanon
                ? "MEETING"
                : "TTRPG"
          const segments = [i18n._(msg`Campaigns`).toUpperCase(), systemLabel]
          if (dmUserId && dmName) {
            segments.push(`${i18n._(msg`DM`)}: ${dmName.toUpperCase()}`)
          }
          return (
            <VoxCampaignHero
              imageUrl={heroImage}
              eyebrowSegments={segments}
              title={series.name}
              subline={series.description || undefined}
              actions={
                <VoxPillButton variant="arcane" leading="●" onClick={() => setShowNewSession(true)}>
                  <Trans>Begin session</Trans>
                </VoxPillButton>
              }
            />
          )
        })()
      ) : (
        <Group justify="space-between" mb="xl" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Title order={2}>{series.name}</Title>
            {series.templateId === DEFAULT_TEMPLATE_FOR_PRODUCT.voxcanon && (
              <Badge color="brand" variant="light">
                <Trans>D&amp;D Companion</Trans>
              </Badge>
            )}
            {series.templateId && series.templateId !== DEFAULT_TEMPLATE_FOR_PRODUCT.voxcanon && (
              <Badge color="info" variant="light">
                {series.templateId}
              </Badge>
            )}
            {dmUserId && dmName && (
              <Badge color="highlight" variant="filled">
                <Trans>DM:</Trans> {dmName}
              </Badge>
            )}
          </Group>
        </Group>
      )}

      <Tabs
        value={activeTab}
        onChange={(v) => {
          handleTabChange(v)
          if (v === "settings") handleSettingsTabOpen()
        }}
      >
        <Tabs.List mb="lg">
          <Tabs.Tab value="overview">
            <Trans>Overview</Trans>
          </Tabs.Tab>
          <Tabs.Tab value="settings">
            <Trans>Settings</Trans>
          </Tabs.Tab>
          <Tabs.Tab value="characters">
            <Trans>Characters</Trans>
          </Tabs.Tab>
          <Tabs.Tab value="chronicle">
            <Trans>Chronicle</Trans>
          </Tabs.Tab>
          <Tabs.Tab value="journals">
            <Trans>Journals</Trans>
          </Tabs.Tab>
          <Tabs.Tab
            value="npcs"
            rightSection={
              pendingNpcsCount > 0 ? (
                <Badge size="xs" color="warning" variant="filled" circle>
                  {pendingNpcsCount}
                </Badge>
              ) : undefined
            }
          >
            <Trans>NPCs</Trans>
          </Tabs.Tab>
          <Tabs.Tab
            value="locations"
            rightSection={
              pendingLocationsCount > 0 ? (
                <Badge size="xs" color="warning" variant="filled" circle>
                  {pendingLocationsCount}
                </Badge>
              ) : undefined
            }
          >
            <Trans>Locations</Trans>
          </Tabs.Tab>
          <Tabs.Tab
            value="factions"
            rightSection={
              pendingFactionsCount > 0 ? (
                <Badge size="xs" color="warning" variant="filled" circle>
                  {pendingFactionsCount}
                </Badge>
              ) : undefined
            }
          >
            <Trans>Factions</Trans>
          </Tabs.Tab>
        </Tabs.List>

        {/* ── Overview tab ─────────────────────────────────────────────── */}
        <Tabs.Panel value="overview">
          <Stack gap="md">
            {series.description && <Text c="dimmed">{series.description}</Text>}

            <Title order={4}>
              <Trans>Cast</Trans>
            </Title>

            {charactersLoading && (
              <Center h={200}>
                <Loader />
              </Center>
            )}

            {!charactersLoading && (!characters || characters.length === 0) && (
              <Text c="dimmed" size="sm">
                <Trans>No characters yet. Go to the Characters tab to add them.</Trans>
              </Text>
            )}

            {!charactersLoading && characters && characters.length > 0 && (
              <SimpleGrid cols={{ base: 2, sm: 3, lg: 4 }}>
                {characters.map((character) => (
                  <Card key={character.id} withBorder padding="sm">
                    <Stack align="center" gap="xs">
                      {character.avatarUrl ? (
                        <Avatar src={character.avatarUrl} size="xl" radius="md" />
                      ) : (
                        <Avatar size="xl" radius="md" color="brand">
                          {speakerInitials(character.name)}
                        </Avatar>
                      )}
                      <Text fw={600} size="sm" ta="center" lineClamp={1}>
                        {character.name}
                      </Text>
                      {character.characterClass && (
                        <Badge color="gray" variant="outline" size="xs">
                          {character.characterClass}
                        </Badge>
                      )}
                      {character.metadata?.isDm === true ? (
                        <Badge color="highlight" variant="light" size="xs">
                          <Trans>DM / NPC</Trans>
                        </Badge>
                      ) : character.playerName ? (
                        <Text size="xs" c="dimmed" ta="center">
                          {character.playerName}
                        </Text>
                      ) : null}
                      {character.playStyle && (
                        <Badge color={getPlayStyleColor(character.playStyle)} variant="light" size="xs">
                          {character.playStyle}
                        </Badge>
                      )}
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ── Settings tab ─────────────────────────────────────────────── */}
        <Tabs.Panel value="settings">
          <form onSubmit={settingsForm.onSubmit((v) => updateSeriesMutation.mutate(v))}>
            <Stack gap="xl" maw={600}>
              <div>
                <Title order={5} mb={4}>
                  <Trans>Analysis prompt</Trans>
                </Title>
                <Text size="sm" c="dimmed" mb="sm">
                  <Trans>
                    Injected before the template system prompt. Use this to set the campaign context, tone, or special
                    instructions for the AI analysis.
                  </Trans>
                </Text>
                <Textarea
                  placeholder={i18n._(
                    msg`e.g. This is a dark fantasy campaign set in a gothic city. Focus on political intrigue and moral ambiguity.`,
                  )}
                  rows={4}
                  key={settingsForm.key("systemPrompt")}
                  {...settingsForm.getInputProps("systemPrompt")}
                />
              </div>

              <div>
                <Title order={5} mb={4}>
                  <Trans>World setting</Trans>
                </Title>
                <Text size="sm" c="dimmed" mb="sm">
                  <Trans>
                    Brief description of the campaign world or current location. Appended to the analysis context.
                  </Trans>
                </Text>
                <TextInput
                  placeholder={i18n._(msg`e.g. "Ravenloft — a cursed land perpetually shrouded in mist"`)}
                  key={settingsForm.key("setting")}
                  {...settingsForm.getInputProps("setting")}
                />
              </div>

              <div>
                <Title order={5} mb={4}>
                  <Trans>Narrative tone</Trans>
                </Title>
                <Text size="sm" c="dimmed" mb="sm">
                  <Trans>Guides the writing style for session recaps and reports.</Trans>
                </Text>
                <TextInput
                  placeholder={i18n._(msg`e.g. "Epic and dramatic, with hints of dark humor"`)}
                  key={settingsForm.key("tone")}
                  {...settingsForm.getInputProps("tone")}
                />
              </div>

              <div>
                <Title order={5} mb={4}>
                  <Trans>Image style</Trans>
                </Title>
                <Text size="sm" c="dimmed" mb="sm">
                  <Trans>Prepended to every scene image prompt. Complements the template art style.</Trans>
                </Text>
                <Textarea
                  placeholder={i18n._(msg`e.g. "dark gothic atmosphere, oil painting, dramatic lighting"`)}
                  rows={3}
                  key={settingsForm.key("imageStyle")}
                  {...settingsForm.getInputProps("imageStyle")}
                />
              </div>

              <div>
                <Title order={5} mb={4}>
                  <Trans>Dungeon Master</Trans>
                </Title>
                <Text size="sm" c="dimmed" mb="sm">
                  <Trans>
                    The DM can edit the campaign chronicle, reassign journal entries between characters, and accept or
                    reject any pending canon. Players can only act on entries for their own character.
                  </Trans>
                </Text>
                <Select
                  placeholder={i18n._(msg`Pick a campaign DM`)}
                  data={memberOptions}
                  clearable
                  searchable
                  key={settingsForm.key("dmUserId")}
                  {...settingsForm.getInputProps("dmUserId")}
                />
              </div>

              <Group>
                <Button type="submit" loading={updateSeriesMutation.isPending}>
                  <Trans>Save settings</Trans>
                </Button>
              </Group>
            </Stack>
          </form>
        </Tabs.Panel>

        {/* ── Characters tab ───────────────────────────────────────────── */}
        <Tabs.Panel value="characters">
          <Group justify="space-between" mb="md">
            <Title order={4}>
              <Trans>Characters</Trans>
            </Title>
            <Button size="sm" onClick={handleOpenCreateCharacter}>
              <Trans>Add character</Trans>
            </Button>
          </Group>

          {charactersLoading && (
            <Center h={200}>
              <Loader />
            </Center>
          )}

          {!charactersLoading && characters?.length === 0 && (
            <Center py="xl">
              <Stack align="center" gap="md">
                <Text size="2rem" style={{ opacity: 0.3 }}>
                  🧙
                </Text>
                <Text c="dimmed" size="sm">
                  <Trans>No characters yet. Add the first one!</Trans>
                </Text>
                <Button variant="light" size="sm" onClick={handleOpenCreateCharacter}>
                  <Trans>Add character</Trans>
                </Button>
              </Stack>
            </Center>
          )}

          {!charactersLoading && characters && characters.length > 0 && (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
              {characters.map((character) => (
                <Card key={character.id} withBorder>
                  <Stack className={styles.characterCard}>
                    <Group wrap="nowrap">
                      {character.avatarUrl ? (
                        <Avatar src={character.avatarUrl} size="lg" radius="md" />
                      ) : (
                        <Avatar size="lg" radius="md" color="brand">
                          {speakerInitials(character.name)}
                        </Avatar>
                      )}
                      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                        <Text className={styles.characterName} lineClamp={1}>
                          {character.name}
                        </Text>
                        {character.metadata?.isDm === true ? (
                          <Badge color="highlight" variant="light" size="xs" w="fit-content">
                            <Trans>DM / NPC</Trans>
                          </Badge>
                        ) : character.playerName ? (
                          <Text size="sm" c="dimmed" lineClamp={1}>
                            {character.playerName}
                          </Text>
                        ) : null}
                      </Stack>
                      <Group gap={4}>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          aria-label={i18n._(msg`Edit character`)}
                          onClick={() => handleOpenEditCharacter(character)}
                        >
                          ✎
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="danger"
                          size="sm"
                          aria-label={i18n._(msg`Remove character`)}
                          loading={deleteCharacterMutation.isPending}
                          onClick={() => handleDeleteCharacter(character)}
                        >
                          ✕
                        </ActionIcon>
                      </Group>
                    </Group>

                    <div className={styles.characterMeta}>
                      {character.characterClass && (
                        <Badge color="gray" variant="outline" size="sm">
                          {character.characterClass}
                        </Badge>
                      )}
                      {character.playStyle && (
                        <Badge color={getPlayStyleColor(character.playStyle)} variant="light" size="sm">
                          {character.playStyle}
                        </Badge>
                      )}
                    </div>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </Tabs.Panel>

        {/* ── Chronicle tab ────────────────────────────────────────────── */}
        <Tabs.Panel value="chronicle">
          <CampaignChronicleView seriesId={id} />
        </Tabs.Panel>

        {/* ── Journals tab ─────────────────────────────────────────────── */}
        <Tabs.Panel value="journals">
          <Stack gap="lg">
            {!charactersLoading && (!characters || characters.length === 0) && (
              <Text c="dimmed" size="sm">
                <Trans>Add characters first to see their journals here.</Trans>
              </Text>
            )}
            {characters?.map((character) => (
              <Card key={character.id} withBorder padding="md">
                <CharacterJournalView seriesId={id} characterId={character.id} characterName={character.name} />
              </Card>
            ))}
          </Stack>
        </Tabs.Panel>

        {/* ── NPCs tab (Phase WS-5) ────────────────────────────────────── */}
        <Tabs.Panel value="npcs">
          <NPCsView
            seriesId={id}
            initialEntityId={search.tab === "npcs" ? search.entityId : undefined}
            initialCreateName={search.tab === "npcs" ? search.createName : undefined}
          />
        </Tabs.Panel>

        {/* ── Locations tab (Phase WS-6) ───────────────────────────────── */}
        <Tabs.Panel value="locations">
          <LocationsView
            seriesId={id}
            initialEntityId={search.tab === "locations" ? search.entityId : undefined}
            initialCreateName={search.tab === "locations" ? search.createName : undefined}
          />
        </Tabs.Panel>

        {/* ── Factions tab (Phase WS-7) ────────────────────────────────── */}
        <Tabs.Panel value="factions">
          <FactionsView seriesId={id} initialEntityId={search.tab === "factions" ? search.entityId : undefined} />
        </Tabs.Panel>
      </Tabs>

      {/* Character create/edit modal */}
      <Modal
        opened={characterModalOpen}
        onClose={handleCharacterModalClose}
        title={editingCharacter ? i18n._(msg`Edit character`) : i18n._(msg`Add character`)}
        size="lg"
      >
        <form onSubmit={characterForm.onSubmit(handleCharacterSubmit)}>
          <Stack>
            <TextInput
              label={i18n._(msg`Name`)}
              placeholder="Thorin Oakenshield"
              required
              key={characterForm.key("name")}
              {...characterForm.getInputProps("name")}
            />

            <div>
              <Text size="sm" fw={500} mb={4}>
                <Trans>Character type</Trans>
              </Text>
              <SegmentedControl
                data={[
                  { value: "player", label: i18n._(msg`Player character`) },
                  { value: "dm", label: i18n._(msg`DM / NPC`) },
                ]}
                key={characterForm.key("role")}
                {...characterForm.getInputProps("role")}
                fullWidth
              />
            </div>

            {characterForm.getValues().role === "player" && (
              <Select
                label={i18n._(msg`Player`)}
                description={i18n._(msg`Which player's account plays this character`)}
                placeholder={i18n._(msg`Unassigned`)}
                data={memberOptions}
                clearable
                key={characterForm.key("userId")}
                {...characterForm.getInputProps("userId")}
              />
            )}

            <TextInput
              label={
                characterForm.getValues().role === "dm"
                  ? i18n._(msg`Controlled by`)
                  : i18n._(msg`Player name (display)`)
              }
              placeholder={
                characterForm.getValues().role === "dm"
                  ? i18n._(msg`e.g. DM, Game Master`)
                  : i18n._(msg`Player's real name`)
              }
              key={characterForm.key("playerName")}
              {...characterForm.getInputProps("playerName")}
            />

            <TextInput
              label={i18n._(msg`Class`)}
              placeholder={i18n._(msg`Fighter, Wizard, Rogue...`)}
              key={characterForm.key("characterClass")}
              {...characterForm.getInputProps("characterClass")}
            />

            <Select
              label={i18n._(msg`Play style`)}
              placeholder={i18n._(msg`Choose a play style`)}
              data={PLAY_STYLE_OPTIONS}
              clearable
              key={characterForm.key("playStyle")}
              {...characterForm.getInputProps("playStyle")}
            />

            <Textarea
              label={i18n._(msg`Backstory`)}
              placeholder={i18n._(msg`Character background and history...`)}
              rows={3}
              key={characterForm.key("backstory")}
              {...characterForm.getInputProps("backstory")}
            />

            <Textarea
              label={i18n._(msg`Image style notes`)}
              description={i18n._(
                msg`Style hints for this character in scene images (e.g. 'dark robes, glowing eyes')`,
              )}
              placeholder={i18n._(msg`Optional style notes...`)}
              rows={2}
              key={characterForm.key("imagePrompt")}
              {...characterForm.getInputProps("imagePrompt")}
            />

            {/* Avatar image */}
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                <Trans>
                  Avatar image{" "}
                  <Text span c="dimmed" size="xs">
                    (portrait / face)
                  </Text>
                </Trans>
              </Text>
              <div className={styles.imagePickerArea}>
                {currentAvatarUrl && (
                  <Image src={currentAvatarUrl} className={styles.imagePreview} alt={i18n._(msg`Avatar preview`)} />
                )}
                <Group gap="xs">
                  <Button variant="light" size="xs" onClick={() => avatarInputRef.current?.click()}>
                    {currentAvatarUrl ? <Trans>Replace</Trans> : <Trans>Upload image</Trans>}
                  </Button>
                  {currentAvatarUrl && (
                    <Button
                      variant="subtle"
                      color="danger"
                      size="xs"
                      onClick={() => {
                        setPendingAvatarFile(null)
                        setPendingAvatarPreview(null)
                      }}
                    >
                      <Trans>Remove</Trans>
                    </Button>
                  )}
                </Group>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleAvatarFileChange}
                />
              </div>
            </Stack>

            {/* Full-body image */}
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                <Trans>
                  Full-body image{" "}
                  <Text span c="dimmed" size="xs">
                    (optional, for wide-shot scenes)
                  </Text>
                </Trans>
              </Text>
              <div className={styles.imagePickerArea}>
                {currentFullBodyUrl && (
                  <Image
                    src={currentFullBodyUrl}
                    className={styles.imagePreview}
                    alt={i18n._(msg`Full-body preview`)}
                  />
                )}
                <Group gap="xs">
                  <Button variant="light" size="xs" onClick={() => fullBodyInputRef.current?.click()}>
                    {currentFullBodyUrl ? <Trans>Replace</Trans> : <Trans>Upload image</Trans>}
                  </Button>
                  {currentFullBodyUrl && (
                    <Button
                      variant="subtle"
                      color="danger"
                      size="xs"
                      onClick={() => {
                        setPendingFullBodyFile(null)
                        setPendingFullBodyPreview(null)
                      }}
                    >
                      <Trans>Remove</Trans>
                    </Button>
                  )}
                </Group>
                <input
                  ref={fullBodyInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleFullBodyFileChange}
                />
              </div>
            </Stack>

            <Group justify="flex-end">
              <Button variant="subtle" onClick={handleCharacterModalClose}>
                <Trans>Cancel</Trans>
              </Button>
              <Button type="submit" loading={isCharacterMutating}>
                {editingCharacter ? <Trans>Save</Trans> : <Trans>Add character</Trans>}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <NewMeetingDialog open={showNewSession} onClose={() => setShowNewSession(false)} />
    </Container>
  )
}

interface ICampaignSearch {
  tab?: "overview" | "settings" | "characters" | "chronicle" | "journals" | "npcs" | "locations" | "factions"
  entityId?: string
  /** Pre-fills the create form on the matching world tab — used by chronicle's
   * click-to-resolve flow when the DM wants to convert an orphan name mention
   * into a curated NPC / location entry. */
  createName?: string
}

export const Route = createFileRoute("/_auth/campaigns_/$id")({
  // Campaigns are a Voxcanon concept. Lexicanon goes home, same gate as the list.
  beforeLoad: () => {
    const bounced = voxcanonOnlyRedirect(detectProduct(), { to: "/" })
    if (bounced) throw bounced
  },
  component: CampaignDetailPage,
  validateSearch: (search: Record<string, unknown>): ICampaignSearch => {
    const out: ICampaignSearch = {}
    const t = search.tab
    if (
      typeof t === "string" &&
      ["overview", "settings", "characters", "chronicle", "journals", "npcs", "locations", "factions"].includes(t)
    ) {
      out.tab = t as ICampaignSearch["tab"]
    }
    if (typeof search.entityId === "string") out.entityId = search.entityId
    if (typeof search.createName === "string") out.createName = search.createName
    return out
  },
})
